// ============================================================
// MismatchCheck.tsx — (Audit fix batch 1, 31 Oct, H2) the final bill and the
// statement differ, PART BY PART (profit costs / success fee / disbursements —
// VAT in, issued credit notes off; backend lib/settlementStatement
// billStatementMismatch). Neutral: it shows both figures for each part and
// never says which document to change. It BLOCKS Issue and the pack (email,
// post or hand) until someone ticks "I've checked the difference" and gives a
// reason — sent as `mismatchOverride`, recorded on the statement (who / when /
// why) and in the history. A difference already checked (e.g. at issue) is
// shown as checked and needs nothing more.
// Used by StatementSendModal (the pack).
// (Audit B2, low b) At ISSUE (DraftEditor) it is IssueMismatchCheck below:
// the check (tick + reason) is RECORDED first, then an approver OKs it, and
// someone other than that approver issues — both names are kept.
// ============================================================
import React, { useId, useState } from 'react'
import { MISMATCH_REASON_MAX, type BillMismatch, type MismatchOverrideBody, type StatementDraft } from '../../../types/settlementStatement.types'
import ActionBox from '../../shared/ActionBox'
import FormField from '../../shared/FormField'
import FieldError from '../../shared/FieldError'
import { B_SECONDARY, BIcon, INPUT, NOTE, money } from '../../billing/billingUi'
import { ukDate } from '../../../utils/civilDate'

export interface MismatchValue { checked: boolean; reason: string }
export const EMPTY_MISMATCH: MismatchValue = { checked: false, reason: '' }
const cap = (s: string): string => s.charAt(0).toUpperCase() + s.slice(1)
// What the request carries — null while the tick or the reason is missing.
export const mismatchOverrideOf = (v: MismatchValue): MismatchOverrideBody | null =>
  v.checked && v.reason.trim() ? { checked: true, reason: v.reason.trim() } : null
// Why the action is blocked (visible text next to the button), or null.
export const mismatchBlockedReason = (m: BillMismatch | null | undefined, overridden: boolean | undefined, v: MismatchValue): string | null => {
  if (!m || overridden) return null
  if (!v.checked) return 'The final bill and this statement differ — check them and tick “I’ve checked the difference”.'
  if (!v.reason.trim()) return 'Say why the bill and the statement can differ.'
  return null
}

interface Props {
  mismatch: BillMismatch
  overridden?: boolean
  value: MismatchValue
  onChange: (v: MismatchValue) => void
  disabled?: boolean
  // Show the reason's error (after a press of the action).
  showErrors?: boolean
}
const MismatchCheck: React.FC<Props> = ({ mismatch, overridden, value, onChange, disabled, showErrors }) => {
  const id = useId()
  const reasonErr = value.checked && !value.reason.trim() ? 'Say why the bill and the statement can differ.' : null
  return (
    // (Audit fix batch 2) The new shell: the comparison in the card body; the
    // tick + reason in a plain WHITE section beneath it (never on a tint).
    <ActionBox tone="warn" title={`The final bill ${mismatch.billNumber} and this statement differ`}
      form={overridden ? undefined : (
        <div className="flex flex-col gap-[10px] max-w-[640px]">
          <label className="inline-flex items-start gap-[8px] text-[13.5px] font-semibold text-casevo-forest">
            <input type="checkbox" className="mt-[3px]" checked={value.checked} disabled={disabled}
              onChange={(e) => onChange({ ...value, checked: e.target.checked })} />
            I’ve checked the difference
          </label>
          {value.checked && (
            <FormField label="Why the bill and the statement can differ" required variant="info" htmlFor={`${id}-reason`}>
              <textarea id={`${id}-reason`} className={`${INPUT} h-auto min-h-[72px] py-2`} value={value.reason} maxLength={MISMATCH_REASON_MAX} disabled={disabled} required
                onChange={(e) => onChange({ ...value, reason: e.target.value })}
                aria-invalid={(showErrors && !!reasonErr) || undefined} aria-describedby={`${id}-help${showErrors && reasonErr ? ` ${id}-err` : ''}`} />
              <span id={`${id}-err`}>{showErrors && <FieldError message={reasonErr ?? undefined} />}</span>
              <span id={`${id}-help`} className={`${NOTE} block mt-[4px]`}>Recorded with your name and the time, and shown on the statement. {value.reason.length} / {MISMATCH_REASON_MAX}</span>
            </FormField>
          )}
        </div>
      )}>
      <PartsTable mismatch={mismatch} />
      {overridden && (
        <p className="m-0 mt-[6px] text-[13px] text-casevo-forest flex items-center gap-[6px]"><BIcon name="check" size={13} />This difference has already been checked (see the statement’s history).</p>
      )}
    </ActionBox>
  )
}

export default MismatchCheck

// The parts that differ, both figures side by side (neutral).
const PartsTable: React.FC<{ mismatch: BillMismatch }> = ({ mismatch }) => (
  <>
    <table className="w-full mt-[6px] text-[13px] text-casevo-forest border-collapse">
      <caption className="sr-only">The parts that differ</caption>
      <thead>
        <tr className="text-left text-[12px] text-casevo-text-soft">
          <th scope="col" className="font-medium pb-[4px]">Part</th>
          <th scope="col" className="font-medium pb-[4px] text-right">Bill</th>
          <th scope="col" className="font-medium pb-[4px] text-right">Statement</th>
        </tr>
      </thead>
      <tbody>
        {mismatch.parts.map((p) => (
          <tr key={p.part} className="border-t border-casevo-border-soft">
            <th scope="row" className="font-normal text-left py-[4px]">{cap(p.label)}</th>
            <td className="py-[4px] text-right tabular-nums">{money(p.billPence)}</td>
            <td className="py-[4px] text-right tabular-nums">{money(p.statementPence)}</td>
          </tr>
        ))}
      </tbody>
    </table>
    <p className={`${NOTE} m-0 mt-[6px]`}>Check which one is right before issuing or sending. Each figure includes VAT, less any credit notes.</p>
  </>
)

// ── (Audit B2, low b) The difference at ISSUE: checked, then OK'd ──
// 1. Anyone on the case ticks "I've checked the difference" with the reason
//    and records it (it's for THIS difference — a new one asks again).
// 2. An approver (an admin, or someone who can approve bills) who didn't
//    record it OKs it — the firm's only approver may OK their own.
// 3. Someone other than that approver issues the statement.
const day = (iso: string | null | undefined): string => (iso ? ukDate(String(iso).slice(0, 10)) : '')
export const IssueMismatchCheck: React.FC<{
  draft: Pick<StatementDraft, 'billMismatch' | 'billMismatchCheck' | 'billMismatchOverridden'>
  canApprove: boolean; onlyApprover: boolean; busy: string | null
  record: (reason: string) => Promise<boolean>
  approve: () => Promise<boolean>
}> = ({ draft, canApprove, onlyApprover, busy, record, approve }) => {
  const id = useId()
  const [v, setV] = useState<MismatchValue>(EMPTY_MISMATCH)
  const [tried, setTried] = useState(false)
  const m = draft.billMismatch
  if (!m) return null
  const c = draft.billMismatchCheck ?? null
  const reasonErr = v.checked && !v.reason.trim() ? 'Say why the bill and the statement can differ.' : null
  const save = async (): Promise<void> => {
    setTried(true)
    if (!v.checked || reasonErr) return
    if (await record(v.reason.trim())) { setV(EMPTY_MISMATCH); setTried(false) }
  }
  const approved = c?.approved ?? null
  const ownCheck = !!c?.mine && !onlyApprover
  const form = !c ? (
    <div className="flex flex-col gap-[10px] max-w-[640px]">
      <label className="inline-flex items-start gap-[8px] text-[13.5px] font-semibold text-casevo-forest">
        <input type="checkbox" className="mt-[3px]" checked={v.checked} disabled={!!busy} onChange={(e) => setV({ ...v, checked: e.target.checked })} />
        I’ve checked the difference
      </label>
      {v.checked && (
        <FormField label="Why the bill and the statement can differ" required variant="info" htmlFor={`${id}-reason`}>
          <textarea id={`${id}-reason`} className={`${INPUT} h-auto min-h-[72px] py-2`} value={v.reason} maxLength={MISMATCH_REASON_MAX} disabled={!!busy} required
            onChange={(e) => setV({ ...v, reason: e.target.value })}
            aria-invalid={(tried && !!reasonErr) || undefined} aria-describedby={`${id}-help${tried && reasonErr ? ` ${id}-err` : ''}`} />
          <span id={`${id}-err`}>{tried && <FieldError message={reasonErr ?? undefined} />}</span>
          <span id={`${id}-help`} className={`${NOTE} block mt-[4px]`}>Recorded with your name and the time. An approver then OKs it, and someone other than that approver issues the statement. {v.reason.length} / {MISMATCH_REASON_MAX}</span>
        </FormField>
      )}
      <div className="flex items-center gap-[10px] flex-wrap">
        <button type="button" className={B_SECONDARY} disabled={!!busy || !v.checked} onClick={() => { void save() }}>{busy === 'mismatchCheck' ? 'Recording…' : 'Record the check'}</button>
        {!v.checked && <span className={NOTE}>Tick “I’ve checked the difference” first.</span>}
      </div>
    </div>
  ) : !approved ? (
    <div className="flex items-center gap-[10px] flex-wrap">
      {canApprove && !ownCheck
        ? <button type="button" className={B_SECONDARY} disabled={!!busy} onClick={() => { void approve() }}>{busy === 'mismatchApprove' ? 'Saving…' : 'OK the difference'}</button>
        : <span className={NOTE}>{canApprove ? 'You recorded this check, so another approver needs to OK it.' : 'Waiting for an approver (an admin, or someone who can approve bills) to OK it.'}</span>}
    </div>
  ) : undefined
  return (
    <ActionBox tone={approved ? 'done' : 'warn'} title={`The final bill ${m.billNumber} and this statement differ`} form={form}>
      <PartsTable mismatch={m} />
      {c && (
        <p className="m-0 mt-[8px] text-[13px] text-casevo-forest" data-mismatch-check="">
          <span className="font-semibold">Checked by {c.by}</span>{c.at ? ` on ${day(c.at)}` : ''} — ‘{c.reason}’
          {approved
            ? <span className="flex items-center gap-[6px] mt-[2px]"><BIcon name="check" size={13} />OK’d by {approved.by}{approved.at ? ` on ${day(approved.at)}` : ''}{approved.self ? ' (the firm’s only approver)' : ''}.</span>
            : <span className="block mt-[2px] text-casevo-text-soft">Waiting for an approver to OK it.</span>}
        </p>
      )}
    </ActionBox>
  )
}
